import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useStepWorkspaceRevision } from '../context/StepWorkspaceContext';
import { Link } from 'react-router-dom';
import Header, { BreadcrumbItem } from '../components/Header';
import ContentReader from '../components/ContentReader';
import HistoryButton from '../components/HistoryButton';
import StepHelpButton from '../components/StepHelpButton';
import HistoryModal from '../components/HistoryModal';
import VideoModal from '../components/VideoModal';
import { progressService } from '../services/progressService';
import { useAuthErrorHandler } from '../hooks/useAuthErrorHandler';
import { AuthErrorDisplay } from '../components/AuthErrorDisplay';
import { mergeStepActions, stepDataWithActions, buildStepDataWithActions } from '../utils/mergeStepActions';
import { getStepActionSchema } from '../config/stepActionSchemas';
import styles from '../styles/stepCommon.module.css';
import StepActions, { ActionItem } from '../components/StepActions';
import StepLoginPrompt from '../components/StepLoginPrompt';
import Step1WritingModal from '../components/Step1WritingModal';
import Step1PresentationFields from '../components/Step1PresentationFields';
import {
  normalizeStep1Presentation,
  Step1PresentationRecord,
} from '../types/step1Presentation';
import { Step1WritingMetadata, isStep1PathSelected } from '../types/step1Writing';
import { migrateStep1WritingIfNeeded } from '../utils/step1WritingMigration';
import { formatStep1ProgressLine } from '../utils/step1WritingProgressSummary';
import nudgeStyles from '../styles/threeCirclesModal.module.css';

const GETTING_STARTED_NUDGE_KEY = 'dismissed-getting-started-nudge';

interface Step1Props {
  user?: any;
  onLogin: () => void;
  onLogout: () => void;
}

const Step1: React.FC<Step1Props> = ({ user, onLogin, onLogout }) => {
  const stepWorkspaceRevision = useStepWorkspaceRevision();
  const [isContentReaderOpen, setIsContentReaderOpen] = useState(false);
  const [isGuidanceOpen, setIsGuidanceOpen] = useState(false);
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [isWritingModalOpen, setIsWritingModalOpen] = useState(false);
  const [collapsedActions, setCollapsedActions] = useState<Set<number>>(new Set());
  // Define the original action structure
  const originalActions: ActionItem[] = [
    {
      id: 1,
      title: "Understand what Step One in SAA is about and why it's important",
      description: "Understand how my addictive behavior made life unmanageable and why surrender matters",
      completed: false,
      hasButton: true,
      buttonText: "Read Green Book",
      buttonAction: () => setIsContentReaderOpen(true)
    },
    {
      id: 2,
      title: "How to work Step One (guidance & video)",
      description: "Read guidance and watch video on how to work through Step One",
      completed: false,
      buttons: [
        {
          text: "Read Guidance",
          action: () => setIsGuidanceOpen(true),
          variant: 'primary'
        },
        {
          text: "Watch Video",
          action: () => setIsVideoModalOpen(true),
          variant: 'secondary'
        }
      ]
    },
    {
      id: 3,
      title: "Prepare my First Step story",
      description:
        "Draft or update your First Step in the writing workspace. View, download, or share your story when you're ready.",
      completed: false,
      hasButton: true,
      buttonText: "First Step writing workspace",
      buttonAction: () => setIsWritingModalOpen(true)
    },
    {
      id: 4,
      title: "Talk with my sponsor about my First Step",
      description:
        "It's important that I review my First Step with a sponsor or other trusted person before presenting it.",
      completed: false
    },
    {
      id: 5,
      title: "Read my First Step in a meeting",
      description: "Share your Step One work in an SAA meeting",
      completed: false
    },
    {
      id: 6,
      title: "Take Step One: Admit that I am powerless over compulsive sexual addiction - that my life has become unmanageable",
      description: "Make the final admission that completes Step One",
      completed: false
    }
  ];

  const [actions, setActions] = useState<ActionItem[]>(originalActions);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [stepData, setStepData] = useState<any>(null);
  const [isOptedOut, setIsOptedOut] = useState(false);
  const [gettingStartedComplete, setGettingStartedComplete] = useState(true);
  const [nudgeDismissed, setNudgeDismissed] = useState(
    () => localStorage.getItem(GETTING_STARTED_NUDGE_KEY) === '1'
  );
  const [step1Writing, setStep1Writing] = useState<Step1WritingMetadata | null>(null);
  const [step1Presentation, setStep1Presentation] = useState<Step1PresentationRecord>({});
  const presentationSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { authError, errorMessage, handleError, clearError } = useAuthErrorHandler();

  const breadcrumbs: BreadcrumbItem[] = [
    { label: 'Home', path: '/', active: false },
    { label: 'SAA', path: '/saa', active: false },
    { label: '12 Steps', path: '/saa/12-steps', active: false },
    { label: 'Step One', path: '/saa/12-steps/step-1', active: true }
  ];

  // Initialize progress tracking
  const initializeProgress = async () => {
    if (!user) {
      setIsInitializing(false);
      return;
    }
    
    setIsInitializing(true);
    
    try {
      // Initialize SAA progress
      await progressService.initializeProgress();
      await progressService.migrateGettingStartedIfNeeded();
      
      const gettingStarted = await progressService.getStepProgress('SAA', '12-steps', 0).catch(() => null);
      setGettingStartedComplete(!!gettingStarted?.completed);
      
      // Fetch progress data
      const existingProgress = await progressService.getStepProgress('SAA', '12-steps', 1).catch(() => null);
      
      if (existingProgress) {
        const savedData = existingProgress.data || {};
        setStepData(existingProgress);
        setStep1Presentation(normalizeStep1Presentation(savedData.step1_presentation));

        const { metadata: writingMeta, applied: writingMigration } = migrateStep1WritingIfNeeded(
          savedData,
          true
        );
        setStep1Writing(writingMeta);
        
        // Restore actions state
        if (savedData.actions) {
          const merged = mergeStepActions(1, originalActions, savedData, !!existingProgress.completed);
          setActions(merged.actions);
          if (merged.migrationApplied || writingMigration) {
            await progressService.updateStepProgress({
              program: 'SAA',
              track: '12-steps',
              step_number: 1,
              step_title: 'Step One',
              completed: !!existingProgress.completed,
              data: buildStepDataWithActions(merged.actions, merged.actionSchemaVersion, {
                ...savedData,
                step1_writing: writingMeta,
              }),
            });
          }
        } else if (writingMigration) {
          await progressService.updateStepProgress({
            program: 'SAA',
            track: '12-steps',
            step_number: 1,
            step_title: 'Step One',
            completed: !!existingProgress.completed,
            data: { ...savedData, step1_writing: writingMeta },
          });
        }
      } else {
        const { metadata: writingMeta } = migrateStep1WritingIfNeeded(null, false);
        setStep1Writing(writingMeta);
      }
      
      // Check date tracking preference
      try {
        const journeyData = await progressService.getJourneyProgress();
        setIsOptedOut(journeyData.opted_out);
      } catch (error) {
        console.error('Error loading date tracking preference:', error);
      }
    } catch (err: any) {
      console.error('Error initializing progress:', err);
      handleError(err, 'Failed to load your progress. Please try refreshing the page.');
    } finally {
      setIsInitializing(false);
    }
  };

  useEffect(() => {
    initializeProgress();
  }, [user, stepWorkspaceRevision]);

  const getCurrentActionIndex = () => {
    return actions.findIndex(action => !action.completed);
  };

  const highestCompletedIndex = (() => {
    let idx = -1;
    actions.forEach((action, i) => {
      if (action.completed) idx = i;
    });
    return idx;
  })();

  const isActionAvailable = (actionIndex: number) => {
    if (actionIndex === 0) return true;
    return actions.slice(0, actionIndex).every(a => a.completed);
  };

  const buildStepData = (actionsList: ActionItem[], presentation?: Step1PresentationRecord) => {
    const schemaVersion = getStepActionSchema(1).actionSchemaVersion;
    const extra: Record<string, unknown> = {};
    if (step1Writing) extra.step1_writing = step1Writing;
    const record = presentation ?? step1Presentation;
    const normalized = normalizeStep1Presentation(record);
    if (normalized.presentedDate || normalized.place || normalized.reflection) {
      extra.step1_presentation = normalized;
    }
    return stepDataWithActions(actionsList, schemaVersion, extra);
  };

  const persistPresentation = useCallback(
    async (next: Step1PresentationRecord) => {
      if (!user) return;
      const normalized = normalizeStep1Presentation({
        ...next,
        updatedAt: new Date().toISOString(),
      });
      setStep1Presentation(normalized);
      try {
        const baseData = { ...(stepData?.data || {}), step1_writing: step1Writing };
        if (normalized.presentedDate || normalized.place || normalized.reflection) {
          baseData.step1_presentation = normalized;
        } else {
          delete baseData.step1_presentation;
        }
        const updatedStepData = await progressService.updateStepProgress({
          program: 'SAA',
          track: '12-steps',
          step_number: 1,
          step_title: 'Step One',
          completed: actions.every((a) => a.completed),
          data: buildStepDataWithActions(actions, getStepActionSchema(1).actionSchemaVersion, baseData),
        });
        if (updatedStepData?.step) setStepData(updatedStepData.step);
      } catch (err: unknown) {
        handleError(err, 'Failed to save presentation notes.');
      }
    },
    [user, actions, stepData, step1Writing, handleError]
  );

  const handlePresentationChange = useCallback(
    (next: Step1PresentationRecord) => {
      setStep1Presentation(next);
      if (presentationSaveTimer.current) clearTimeout(presentationSaveTimer.current);
      presentationSaveTimer.current = setTimeout(() => {
        void persistPresentation(next);
      }, 500);
    },
    [persistPresentation]
  );

  useEffect(() => {
    return () => {
      if (presentationSaveTimer.current) clearTimeout(presentationSaveTimer.current);
    };
  }, []);

  const handleSaveStep1Writing = async (metadata: Step1WritingMetadata) => {
    setStep1Writing(metadata);
    try {
      const updatedStepData = await progressService.updateStepProgress({
        program: 'SAA',
        track: '12-steps',
        step_number: 1,
        step_title: 'Step One',
        completed: actions.every((a) => a.completed),
        data: buildStepDataWithActions(actions, getStepActionSchema(1).actionSchemaVersion, {
          ...(stepData?.data || {}),
          step1_writing: metadata,
        }),
      });
      if (updatedStepData?.step) setStepData(updatedStepData.step);
    } catch (err: unknown) {
      handleError(err, 'Failed to save writing progress.');
    }
  };

  const handleCheckboxChange = async (actionId: number, checked: boolean) => {
    const updatedActions = actions.map(action =>
      action.id === actionId ? { ...action, completed: checked } : action
    );
    setActions(updatedActions);

    try {
      await progressService.updateStepProgress({
        program: 'SAA',
        track: '12-steps',
        step_number: 1,
        step_title: 'Step One',
        completed: updatedActions.every(action => action.completed),
        data: buildStepData(updatedActions),
      });
    } catch (err: any) {
      console.error('Error updating action progress:', err);
      handleError(err, 'Failed to save progress. Please try again.');
    }
  };

  const handleButtonClick = (action: ActionItem, e: React.MouseEvent) => {
    e.stopPropagation();
    if (action.buttonAction) {
      action.buttonAction();
    }
  };

  const toggleActionCollapse = (actionId: number) => {
    const newCollapsed = new Set(collapsedActions);
    if (newCollapsed.has(actionId)) {
      newCollapsed.delete(actionId);
    } else {
      newCollapsed.add(actionId);
    }
    setCollapsedActions(newCollapsed);
  };

  // Handle action changes and save to backend
  const handleActionChange = async (updatedActions: ActionItem[]) => {
    if (!user) {
      handleError(new Error('User not logged in'), 'Please sign in to save your progress.');
      return;
    }

    try {
      // Update local state
      setActions(updatedActions);

      // Save to backend
      const allCompleted = updatedActions.every(action => action.completed);
      const updatedStepData = await progressService.updateStepProgress({
        program: 'SAA',
        track: '12-steps',
        step_number: 1,
        step_title: 'Step One',
        completed: allCompleted,
        data: buildStepData(updatedActions),
      });
      
      // Update step data for history modal
      if (updatedStepData && updatedStepData.step) {
        setStepData(updatedStepData.step);
      }



      // Update status note
      const completedCount = updatedActions.filter(a => a.completed).length;
      await progressService.updateStatus({
        program: 'SAA',
        track: '12-steps',
        status_note: `Working Step One - ${completedCount} of 6 actions completed`,
        progress_milestone: allCompleted ? 'step1_completed' : 'step1_in_progress'
      });

    } catch (err: any) {
      console.error('Error updating action progress:', err);
      handleError(err, 'Failed to save progress. Please try again.');
    }
  };

  const allCompleted = actions.every(action => action.completed);

  return (
    <div className={styles.stepPage}>
      <Header 
        breadcrumbs={breadcrumbs}
        user={user}
        onLogin={onLogin}
        onLogout={onLogout}
      />
      
      <main className={styles.mainContent}>
        <div className={styles.pageHeader}>
          <div className={styles.stepTitleContainer}>
            <h1 className={styles.stepTitle}>Step One</h1>
            <StepHelpButton stepKey="app" />
            {stepData && (
              <HistoryButton
                stepNumber={1}
                stepTitle="Step One"
                stepData={stepData}
                isOptedOut={isOptedOut}
                onStepUpdated={() => setIsHistoryModalOpen(true)}
              />
            )}
          </div>
          <p className={styles.stepSubtitle}>
            We admitted we were powerless over compulsive sexual behavior and that our lives had become unmanageable.
          </p>
        </div>

        {!isInitializing && user && !gettingStartedComplete && !nudgeDismissed && (
          <div className={nudgeStyles.gettingStartedNudge}>
            <p>
              We recommend completing{' '}
              <Link to="/saa/12-steps/getting-started">Getting Started</Link> before working Step One.
            </p>
            <button
              type="button"
              className={nudgeStyles.gettingStartedNudgeDismiss}
              onClick={() => {
                localStorage.setItem(GETTING_STARTED_NUDGE_KEY, '1');
                setNudgeDismissed(true);
              }}
              aria-label="Dismiss"
            >
              ×
            </button>
          </div>
        )}

        {isInitializing && (
          <div className={styles.loadingMessage}>
            <p>Loading your progress...</p>
          </div>
        )}

        {errorMessage && (
          <div className={styles.errorMessage}>
            {authError ? (
              <AuthErrorDisplay
                error={errorMessage}
                onLogin={onLogin}
                onRefresh={() => window.location.reload()}
              />
            ) : (
              <>
                <p>{errorMessage}</p>
                <button onClick={() => window.location.reload()}>Refresh Page</button>
              </>
            )}
          </div>
        )}

        {!isInitializing && !errorMessage && (
          <>
            {!user && (
              <StepLoginPrompt onLogin={onLogin} />
            )}
            {user && (
          <div className={styles.actionsContainer}>
            <h2 className={styles.actionsTitle}>Actions to Complete Step One</h2>
            <p className={styles.actionsDescription}>
              Complete each action in order. Check the box when you've finished each action.
            </p>
            
            <StepActions
              actions={actions.map((a, index) => {
                if (a.id === 3) {
                  return {
                    ...a,
                    renderBelowButton: () => {
                      if (!step1Writing || !isStep1PathSelected(step1Writing)) return null;
                      const line = formatStep1ProgressLine(step1Writing);
                      return line ? <span>{line}</span> : null;
                    },
                  };
                }
                if (a.id === 5 && (isActionAvailable(index) || a.completed)) {
                  return {
                    ...a,
                    renderBelowButton: () => (
                      <Step1PresentationFields
                        value={step1Presentation}
                        onChange={handlePresentationChange}
                      />
                    ),
                  };
                }
                return a;
              })}
              onActionChange={handleActionChange}
            />

            {allCompleted && (
              <>
                <div className={styles.completionMessage}>
                  <h3>🎉 Congratulations!</h3>
                  <p>You have completed all actions for Step One and can move on to Step Two.</p>
                </div>
                <div className={styles.stepNavigation}>
                  <div className={styles.navigationLinks}>
                    <a href="/saa/12-steps/getting-started" className={styles.prevStepLink}>
                      {'<<'} Getting Started
                    </a>
                    <a href="/saa/12-steps/step-2" className={styles.nextStepLink}>
                      Step Two {'>>'}
                    </a>
                  </div>
                </div>
              </>
            )}

            {!allCompleted && (
              <div className={styles.stepNavigation}>
                <div className={styles.navigationLinks}>
                  <a href="/saa/12-steps/getting-started" className={styles.prevStepLink}>
                    {'<<'} Getting Started
                  </a>
                  <a href="/saa/12-steps/step-2" className={styles.nextStepLink}>
                    Step Two {'>>'}
                  </a>
                </div>
              </div>
            )}
          </div>
            )}
          </>
        )}
      </main>

      {/* ContentReader for Understanding Step One */}
      <ContentReader
        isOpen={isContentReaderOpen}
        onClose={() => setIsContentReaderOpen(false)}
        filePath="/content/010_CHAPTER_THREE.md"
        startTag="<<step-one start>>"
        endTag="<<step-one end>>"
      />

      {/* ContentReader for Working Step One */}
      <ContentReader
        isOpen={isGuidanceOpen}
        onClose={() => setIsGuidanceOpen(false)}
        filePath="/content/Step_1_Guidance.md"
        startTag="<<working-step-one start>>"
        endTag="<<working-step-one end>>"
      />

      {/* History Modal */}
      <HistoryModal
        isOpen={isHistoryModalOpen}
        onClose={() => setIsHistoryModalOpen(false)}
        stepNumber={1}
        stepTitle="Step One"
        stepData={stepData}
        onStepUpdated={() => {
          setIsHistoryModalOpen(false);
          // Refresh step data
          if (user) {
            initializeProgress();
          }
        }}
      />
      
      <VideoModal
        isOpen={isVideoModalOpen}
        onClose={() => setIsVideoModalOpen(false)}
        videoId="J6-8SICtjww"
        title="Step One Video Guide"
      />
      {step1Writing && (
        <Step1WritingModal
          isOpen={isWritingModalOpen}
          onClose={() => setIsWritingModalOpen(false)}
          user={user}
          metadata={step1Writing}
          onSaveMetadata={handleSaveStep1Writing}
        />
      )}
    </div>
  );
};

export default Step1; 
